(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') api.install(root, document);
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // The hybrid gallery keeps the native first photo as the design slot's thumbnail. This
  // controller mirrors the live design canvas onto that thumbnail once a committed frame is
  // actually visible, so the strip previews the shopper's own design instead of the stock
  // photo — and falls back to the stock photo whenever the canvas has nothing to show.
  // Display-only: it copies pixels with drawImage (safe even from a tainted canvas) and
  // never exports them, and it owns no gallery, slider or runtime behavior.

  var MIRROR_BACKGROUND = '#f7f7f7';
  var MAX_BACKING_SCALE = 2;

  function frameVisible(canvas) {
    return !!canvas && canvas.getAttribute('data-pz-frame-state') !== 'hidden';
  }

  function designCanvas(mount) {
    return mount && mount.querySelector ? mount.querySelector('[data-pz-canvas] canvas') : null;
  }

  function mirrorable(mount) {
    var canvas = designCanvas(mount);
    return !!(canvas && canvas.width > 0 && canvas.height > 0
      && frameVisible(canvas)
      && mount.classList && mount.classList.contains('pz-has-committed-frame'));
  }

  function containRect(sourceWidth, sourceHeight, boxWidth, boxHeight) {
    var scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
    var width = sourceWidth * scale;
    var height = sourceHeight * scale;
    return {
      x: (boxWidth - width) / 2,
      y: (boxHeight - height) / 2,
      width: width,
      height: height
    };
  }

  function createMirror(document, thumbButton) {
    var mirror = document.createElement('canvas');
    mirror.setAttribute('data-pz-live-thumb', '');
    mirror.setAttribute('aria-hidden', 'true');
    mirror.style.position = 'absolute';
    mirror.style.inset = '0';
    mirror.style.width = '100%';
    mirror.style.height = '100%';
    mirror.style.display = 'none';
    mirror.style.background = MIRROR_BACKGROUND;
    mirror.style.pointerEvents = 'none';
    if (!thumbButton.style.position && getComputedStyle(thumbButton).position === 'static') {
      thumbButton.style.position = 'relative';
    }
    thumbButton.appendChild(mirror);
    return mirror;
  }

  function bindMirror(root, document, mount, thumbButton) {
    var mirror = createMirror(document, thumbButton);
    var context = mirror.getContext && mirror.getContext('2d');
    if (!context) {
      mirror.remove();
      return null;
    }
    var drawQueued = false;

    function draw() {
      drawQueued = false;
      if (!document.contains(mount) || !document.contains(mirror)) return;
      if (!mirrorable(mount)) {
        mirror.style.display = 'none';
        return;
      }
      var source = designCanvas(mount);
      var box = thumbButton.getBoundingClientRect();
      if (!box.width || !box.height) return;
      var scale = Math.min(MAX_BACKING_SCALE, root.devicePixelRatio || 1);
      var backingWidth = Math.max(1, Math.round(box.width * scale));
      var backingHeight = Math.max(1, Math.round(box.height * scale));
      if (mirror.width !== backingWidth) mirror.width = backingWidth;
      if (mirror.height !== backingHeight) mirror.height = backingHeight;
      context.fillStyle = MIRROR_BACKGROUND;
      context.fillRect(0, 0, backingWidth, backingHeight);
      var fit = containRect(source.width, source.height, backingWidth, backingHeight);
      try {
        context.drawImage(source, fit.x, fit.y, fit.width, fit.height);
      } catch (error) {
        // A detached or zero-sized source frame must never break the gallery.
        mirror.style.display = 'none';
        return;
      }
      mirror.style.display = 'block';
    }

    function queueDraw() {
      if (drawQueued) return;
      drawQueued = true;
      if (typeof root.requestAnimationFrame === 'function') root.requestAnimationFrame(draw);
      else root.setTimeout(draw, 16);
    }

    // Every committed re-render toggles the mount's frame classes (and the hidden-warm
    // reveal flips data-pz-frame-state), so these attribute edges are the redraw signal.
    var observer = null;
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(queueDraw);
      observer.observe(mount, {
        attributes: true,
        attributeFilter: ['class', 'data-pz-frame-state'],
        subtree: true
      });
    }
    queueDraw();
    return {
      refresh: queueDraw,
      destroy: function () {
        if (observer) observer.disconnect();
        mirror.remove();
      }
    };
  }

  function designSlotThumbButton(document, slot) {
    var mediaId = String(slot && slot.getAttribute && slot.getAttribute('data-media-id') || '').trim();
    if (!mediaId) return null;
    var gallery = slot.closest ? slot.closest('media-gallery') : null;
    var scope = gallery || document;
    var item = null;
    var candidates = scope.querySelectorAll ? scope.querySelectorAll('.thumbnail-list [data-target]') : [];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].getAttribute('data-target') === mediaId) { item = candidates[i]; break; }
    }
    return item ? item.querySelector('button.thumbnail') : null;
  }

  function install(root, document) {
    if (root.__pzGalleryLiveThumbInstalled) return;
    root.__pzGalleryLiveThumbInstalled = true;

    var bound = false;

    function tryBind() {
      if (bound) return true;
      var slot = document.querySelector('[data-pz-gallery-design-slot]');
      if (!slot) return false;
      var gallery = slot.closest && slot.closest('media-gallery');
      var mount = slot.querySelector('[data-pz-personalizer]')
        || (gallery && gallery.querySelector('.pz-gallery-design-overlay[data-pz-personalizer]'));
      if (!mount || (mount.closest && mount.closest('[data-pz-cart-edit-modal]'))) return false;
      var thumbButton = designSlotThumbButton(document, slot);
      if (!thumbButton) return false;
      bound = !!bindMirror(root, document, mount, thumbButton);
      return bound;
    }

    if (tryBind()) return;
    if (typeof MutationObserver !== 'function') return;
    // The design slot appears only after the storefront controller binds the hybrid gallery;
    // the filtered attribute observer fires exactly when that marker lands.
    var slotWatcher = new MutationObserver(function () {
      if (tryBind()) slotWatcher.disconnect();
    });
    slotWatcher.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-pz-gallery-design-slot'],
      subtree: true
    });
  }

  return {
    frameVisible: frameVisible,
    containRect: containRect,
    designSlotThumbButton: designSlotThumbButton,
    install: install
  };
});
